/**
 * Store per la gestione dello stato della chat.
 * 
 * Utilizza Zustand per la gestione dello stato globale.
 */

import { create } from 'zustand';

export type ChatScope = 'global' | 'thread';

export interface SuggestedAction {
  label: string;
  type: 'prompt' | 'action';
  prompt?: string;
  action?: string;
  email_id?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  thinking?: string;
  source_email_ids?: string[];
  suggested_actions?: SuggestedAction[];
}

export interface ChatState {
  // Stato
  isOpen: boolean;
  scope: ChatScope;
  activeCaseId: string | null;
  activeCaseTitle: string | null;
  history: ChatMessage[];
  isLoading: boolean;
  
  // Actions
  setIsOpen: (open: boolean) => void;
  setScope: (scope: ChatScope) => void;
  setActiveCaseId: (caseId: string | null, title?: string | null) => void;
  addMessage: (message: ChatMessage) => void;
  clearHistory: () => void;
  setIsLoading: (loading: boolean) => void;
  reset: () => void;
}

const initialState: Omit<ChatState, 'setIsOpen' | 'setScope' | 'setActiveCaseId' | 'addMessage' | 'clearHistory' | 'setIsLoading' | 'reset'> = {
  isOpen: false,
  scope: 'global' as ChatScope,
  activeCaseId: null,
  activeCaseTitle: null,
  history: [],
  isLoading: false,
};

export const useChatStore = create<ChatState>((set) => ({
  // Stato iniziale
  ...initialState,
  
  // Actions
  setIsOpen: (open) => set({ isOpen: open }),
  setScope: (scope) => set({ 
    scope,
    history: [], // Reset history when scope changes
    isLoading: false, // Reset loading state
  }),
  setActiveCaseId: (caseId, title) => set({ 
    activeCaseId: caseId, 
    activeCaseTitle: title ?? null,
    history: [], // Pulisci cronologia quando cambia il caso
    isLoading: false,
  }),
  addMessage: (message) => set((state) => ({ history: [...state.history, message] })),
  clearHistory: () => set({ history: [], isLoading: false }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  reset: () => set(initialState),
}));
